// The world bake's MainScene passes and baked tiles (docs/platform/RenderFidelity.md "Baked
// tiles"), run on the GPU at match start: per tier the scene quads are drawn into the bake
// canvas, converted into 2048x2048 tiles with the state grid in their alpha, and given the
// original's mip chain. land.ts draws the tiles.
//
// - `WS_Baker::StartBakingProcess` 0xde28a0 queues CleanUp, LogicInfo, then for BG, MID and FG a
//   CleanUp (clear to 0) and a MainScene layer. A MainScene layer renders twice
//   (`m_uMainSceneRenderPassCount` 0 and 1, `EndBakingLayer` 0xde3650) without a clear between;
//   `RenderTextureNode` 0xde1550 sets render flags 0x80 / 0x100 and `WS_SGNodeTexture::ED_Render`
//   0xe2aa20 sends an `XPRIM_QUAD` with the node colour (white on every texture node of the data)
//   and flags | 2, drawn by `__Platform_RenderBatchQuads` 0x1142480 with `XPrim_TexShader`
//   (`texture(sTexture, UV) * colour`, `maskTest` 0). `__SetRenderState` 0x1141510: flag 0x80
//   `BlendFuncSeparate(ONE, ZERO, ZERO, ZERO)`, `ADD/ADD` (the colour of the last quad, alpha 0);
//   flag 0x100 `BlendFuncSeparate(SRC_ALPHA, ONE_MINUS_SRC_ALPHA, ONE, ONE)`, `ADD/MAX`. gdb (hb-A
//   bake, frames 1815-1823): the atlas pages sample `GL_LINEAR`, clamped, without mipmaps.
// - `BakeRender` 0xde15e0 walks the graph three times per pass: plain (the tier's nodes; MID
//   without the buildings' nodes; a node outside a building gets the walk's z as its sub-z,
//   `SetSubZ(z)`), building back walls (MID pass: BG nodes of a building, render priority 0x13,
//   sub-z `subZ * 7 + (1000 + n)`), building land (MID pass: MID nodes of a building, priority
//   0x14). `XPRIM_SortFunc` 0x113a230 orders them.
// - `EndBakingLayer` reads the 8192x4096 canvas back; `WS_BakedLayer::ConvertTextureToTiledTexture_PC`
//   0xe6ee10 cuts it into 2048x2048 cells (rows bottom-up) and writes every pixel with a nonzero
//   alpha byte A as RGB + (state | a3), a3 = 7 above A / 255 > 0.95, else int((A / 255)^2 * 7)
//   (float32), state = the state grid byte; a cell whose alpha bytes are all 0 has no tile.
//   `__UploadIndividualTileToGPU` 0xe6eb60: RGBA8, `GL_NEAREST` magnification,
//   `GL_NEAREST_MIPMAP_NEAREST` minification, clamped, `GL_TEXTURE_LOD_BIAS` -1, the levels of
//   `__BuildMipmaps` 0xe6d950: level l (x, y) from level l-1's (2x, 2y) and (2x + 2, 2y) (2x at
//   the last column; the second row is never read): RGB the truncated mean, alpha the truncated
//   mean of the 3-bit a3 OR the state bits. Checked against gdb dumps of hb-A: the conversion of
//   the read-back canvas and the levels 1, 2 and 11 of all five MID tiles are equal byte for byte.
import type * as THREE from 'three';
import { QUAD_BAKEABLE, QUAD_BUILDING, TIER_BG, TIER_FG, TIER_MID, type SceneQuad, type SceneView } from '../sim/view.ts';

/** Bake canvas and cell sizes (`WS_LayerGrid`: 8192x4096 world units, 2048 a cell, 4x2 cells). */
export const BAKE_W = 8192, BAKE_H = 4096, CELL = 2048, CELLS_X = 4, CELLS_Y = 2;
export const TILE_LEVELS = 12;
/** Bytes of one tile with its mip chain (RGBA8 2048² down to 1x1). */
export const TILE_BYTES = (() => { let n = 0; for (let l = 0; l < TILE_LEVELS; l++) n += (CELL >> l) ** 2 * 4; return n; })();

const PRIORITY_BUILDING_BACK = 0x13, PRIORITY_BUILDING_LAND = 0x14;
const SORT_EPS = Math.fround(1e-9);

/** One draw of a MainScene pass: the quad, its sort keys and its submission order. */
type BakeSub = { q: SceneQuad; priority: number; z: number; seq: number };

/** The draw lists of the three MainScene layers, in `XPRIM_SortFunc` order. A page's GL texture
 * handle (the third sort key) follows the order the original created them: the LogicInfo walk
 * prepares the textures of the MID nodes and of every node inside a building, the BG and FG
 * MainScene walks the rest. */
export function mainSceneLists(scene: SceneView): Map<number, SceneQuad[]> {
  const quads = scene.quads.filter(q => q.flags & QUAD_BAKEABLE);
  const inBuilding = (q: SceneQuad): boolean => (q.flags & QUAD_BUILDING) !== 0;
  const rank = new Map<number, number>();
  const see = (q: SceneQuad): void => { if (!rank.has(q.page)) rank.set(q.page, rank.size); };
  for (const q of quads) if (q.tier === TIER_MID || inBuilding(q)) see(q);
  for (const t of [TIER_BG, TIER_FG]) for (const q of quads) if (q.tier === t) see(q);
  const lists = new Map<number, SceneQuad[]>();
  for (const tier of [TIER_BG, TIER_MID, TIER_FG]) {
    const subs: BakeSub[] = [];
    const add = (q: SceneQuad, priority: number, z: number): void => { subs.push({ q, priority, z, seq: subs.length }); };
    for (const q of quads) {
      if (q.tier !== tier || (tier === TIER_MID && inBuilding(q))) continue;
      add(q, q.priority, inBuilding(q) ? q.z : q.prefabZ);
    }
    if (tier === TIER_MID) {
      let back = 0;
      for (const q of quads) {
        if (q.tier !== TIER_BG || !inBuilding(q)) continue;
        add(q, PRIORITY_BUILDING_BACK, Math.fround(Math.fround(q.z * 7) + Math.fround(1000 + back)));
        back = Math.fround(back + 1);
      }
      for (const q of quads) if (q.tier === TIER_MID && inBuilding(q)) add(q, PRIORITY_BUILDING_LAND, q.z);
    }
    subs.sort((a, b) => {
      if (a.priority !== b.priority) return a.priority - b.priority;
      const d = Math.fround(a.z - b.z);
      if (d > SORT_EPS) return 1;
      if (d < -SORT_EPS) return -1;
      if (a.q.page === b.q.page) return a.seq - b.seq;
      return (rank.get(a.q.page) ?? 0) - (rank.get(b.q.page) ?? 0);
    });
    lists.set(tier, subs.map(s => s.q));
  }
  return lists;
}

/** a3 of `ConvertTextureToTiledTexture_PC` as thresholds: the smallest alpha byte giving 1..7. */
function a3Thresholds(): number[] {
  const inv255 = Math.fround(0.003921568859368563), lim = Math.fround(0.949999988079071);
  const a3 = (a: number): number => {
    const an = Math.fround(a * inv255);
    return an <= lim ? Math.trunc(Math.fround(Math.fround(an * an) * 7)) : 7;
  };
  const t: number[] = [];
  for (let k = 1; k <= 7; k++) { let a = 1; while (a3(a) < k) a++; t.push(a); }
  return t;
}

const FULLSCREEN_VS = `#version 300 es
void main() {
  vec2 p = vec2(float((gl_VertexID << 1) & 2), float(gl_VertexID & 2));
  gl_Position = vec4(p * 2.0 - 1.0, 0.0, 1.0);
}`;

// XPrim_TexShaderV/F as the bake draws them (colour white, maskTest 0); also the lobby preview's
// draws (preview.ts).
export const BAKE_VS = `#version 300 es
in vec2 position;
in vec2 uv;
uniform vec4 xf;
out vec2 UV;
void main() {
  UV = uv;
  gl_Position = vec4(position * xf.xy + xf.zw, 0.0, 1.0);
}`;
export const BAKE_FS = `#version 300 es
precision highp float;
in vec2 UV;
uniform sampler2D sTexture;
out vec4 OUTColor;
void main() {
  OUTColor = texture(sTexture, UV);
}`;

const CONVERT_FS = `#version 300 es
precision highp float;
precision highp int;
precision highp usampler2D;
uniform sampler2D canvas;
uniform usampler2D stateGrid;
uniform uint thr[7];
out vec4 o;
void main() {
  ivec2 p = ivec2(gl_FragCoord.xy);
  uvec4 c = uvec4(round(texelFetch(canvas, p, 0) * 255.0));
  if (c.a == 0u) { o = vec4(0.0); return; }
  uint a3 = 0u;
  for (int k = 0; k < 7; k++) if (c.a >= thr[k]) a3 = uint(k + 1);
  uint s = texelFetch(stateGrid, p, 0).r;
  o = vec4(vec3(c.rgb), float(s | a3)) / 255.0;
}`;

const REDUCE_FS = `#version 300 es
precision highp float;
uniform sampler2D tile;
out vec4 o;
void main() {
  ivec2 b = ivec2(gl_FragCoord.xy) * 32;
  for (int y = 0; y < 32; y++) for (int x = 0; x < 32; x++) {
    if (texelFetch(tile, b + ivec2(x, y), 0).a > 0.0) { o = vec4(1.0); return; }
  }
  o = vec4(0.0);
}`;

const MIP_FS = `#version 300 es
precision highp float;
precision highp int;
uniform sampler2D src;
out vec4 o;
void main() {
  ivec2 p = ivec2(gl_FragCoord.xy);
  int w = textureSize(src, 0).x;
  int x0 = 2 * p.x;
  int x1 = x0 + 2 < w ? x0 + 2 : x0;
  uvec4 a = uvec4(round(texelFetch(src, ivec2(x0, 2 * p.y), 0) * 255.0));
  uvec4 b = uvec4(round(texelFetch(src, ivec2(x1, 2 * p.y), 0) * 255.0));
  uvec3 rgb = (a.rgb + b.rgb) >> 1u;
  uint al = (((a.a & 7u) + (b.a & 7u)) >> 1u) | ((a.a | b.a) & 0xf8u);
  o = vec4(vec3(rgb), float(al)) / 255.0;
}`;

export function program(gl: WebGL2RenderingContext, vs: string, fs: string, attribs: string[] = []): WebGLProgram {
  const shader = (type: number, src: string): WebGLShader => {
    const s = gl.createShader(type)!;
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(`bake shader: ${gl.getShaderInfoLog(s)}`);
    return s;
  };
  const p = gl.createProgram()!;
  const v = shader(gl.VERTEX_SHADER, vs), f = shader(gl.FRAGMENT_SHADER, fs);
  gl.attachShader(p, v);
  gl.attachShader(p, f);
  attribs.forEach((a, i) => gl.bindAttribLocation(p, i, a));
  gl.linkProgram(p);
  gl.deleteShader(v);
  gl.deleteShader(f);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error(`bake program: ${gl.getProgramInfoLog(p)}`);
  return p;
}

/** The WebGL texture three.js holds for `t` (uploaded first). */
export function glTexture(renderer: THREE.WebGLRenderer, t: THREE.Texture): WebGLTexture | null {
  renderer.initTexture(t);
  const props: unknown = renderer.properties.get(t);
  return props && typeof props === 'object' && '__webglTexture' in props && props.__webglTexture instanceof WebGLTexture ? props.__webglTexture : null;
}

export type BakeResult = {
  /** Per tier the 8 cells' tile textures (row-major from the bottom-left cell; null: no tile). */
  tiles: Map<number, (WebGLTexture | null)[]>;
  /** Wall time of the bake (including the GPU work up to the last readback). */
  ms: number;
  /** GPU bytes of the tiles. */
  bytes: number;
};

/** The three MainScene layers on the GPU: tiles with the original's texels and mip chain.
 * `pages`: the atlas page textures by scene page index; `state`: the state grid
 * (`sim::view::land(2)`, 8192x4096, rows bottom-up) or null (no flags). three.js's GL state is
 * reset afterwards. */
export function bakeTiles(renderer: THREE.WebGLRenderer, lists: Map<number, SceneQuad[]>, pages: (THREE.Texture | null)[], state: Uint8Array | null): BakeResult {
  const t0 = performance.now();
  // three.js r170 renders through WebGL2 only.
  const gl = renderer.getContext() as WebGL2RenderingContext;
  const glPages = pages.map(t => (t ? glTexture(renderer, t) : null));
  // The window coordinates of the original's canvas: each cell is drawn with the canvas viewport
  // moved by the cell's origin when the GL allows an 8192 viewport.
  const maxViewport: Int32Array = gl.getParameter(gl.MAX_VIEWPORT_DIMS);
  const fullViewport = maxViewport[0] >= BAKE_W && maxViewport[1] >= BAKE_H;
  const bakeProg = program(gl, BAKE_VS, BAKE_FS, ['position', 'uv']);
  const convertProg = program(gl, FULLSCREEN_VS, CONVERT_FS);
  const reduceProg = program(gl, FULLSCREEN_VS, REDUCE_FS);
  const mipProg = program(gl, FULLSCREEN_VS, MIP_FS);
  const sampler = gl.createSampler()!;
  gl.samplerParameteri(sampler, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.samplerParameteri(sampler, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.samplerParameteri(sampler, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.samplerParameteri(sampler, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  const tex2d = (internal: number, w: number, h: number, levels = 1): WebGLTexture => {
    const t = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, t);
    gl.texStorage2D(gl.TEXTURE_2D, levels, internal, w, h);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, levels > 1 ? gl.NEAREST_MIPMAP_NEAREST : gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    return t;
  };
  const canvas = tex2d(gl.RGBA8, CELL, CELL);
  const stateTex = tex2d(gl.R8UI, CELL, CELL);
  const flags = tex2d(gl.R8, 64, 64);
  const fb = gl.createFramebuffer()!;
  const vao = gl.createVertexArray()!;
  const emptyVao = gl.createVertexArray()!;
  const vbo = gl.createBuffer()!;
  const ibo = gl.createBuffer()!;
  const pixel = new Uint8Array(64 * 64 * 4);
  const thr = new Uint32Array(a3Thresholds());
  gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
  gl.pixelStorei(gl.PACK_ALIGNMENT, 4);
  gl.disable(gl.DEPTH_TEST);
  gl.disable(gl.CULL_FACE);
  gl.disable(gl.SCISSOR_TEST);
  gl.disable(gl.STENCIL_TEST);
  gl.colorMask(true, true, true, true);
  const target = (t: WebGLTexture, level: number): void => {
    gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, t, level);
  };
  const tiles = new Map<number, (WebGLTexture | null)[]>();
  let bytes = 0;
  try {
    for (const [tier, quads] of lists) {
      const cells: (WebGLTexture | null)[] = new Array(CELLS_X * CELLS_Y).fill(null);
      tiles.set(tier, cells);
      if (!quads.length) continue;
      // One stream for the tier: BL, TL, TR, BR (`__Platform_BuildQuads`), indices k+2, k+1, k,
      // k+3, k+2, k of the global IBO; consecutive quads of one page share a draw.
      const v = new Float32Array(quads.length * 16);
      const idx = new Uint32Array(quads.length * 6);
      const runs: { page: number; first: number; count: number }[] = [];
      quads.forEach((q, i) => {
        const [l, t, r, b] = q.uv;
        const c = q.corners;
        v.set([c[0], c[1], l, b, c[2], c[3], l, t, c[4], c[5], r, t, c[6], c[7], r, b], i * 16);
        idx.set([4 * i + 2, 4 * i + 1, 4 * i, 4 * i + 3, 4 * i + 2, 4 * i], i * 6);
        const last = runs[runs.length - 1];
        if (last && last.page === q.page) last.count++;
        else runs.push({ page: q.page, first: i, count: 1 });
      });
      gl.bindVertexArray(vao);
      gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
      gl.bufferData(gl.ARRAY_BUFFER, v, gl.STATIC_DRAW);
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ibo);
      gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, idx, gl.STATIC_DRAW);
      gl.enableVertexAttribArray(0);
      gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 16, 0);
      gl.enableVertexAttribArray(1);
      gl.vertexAttribPointer(1, 2, gl.FLOAT, false, 16, 8);
      for (let cell = 0; cell < CELLS_X * CELLS_Y; cell++) {
        const cx = cell % CELLS_X, cy = (cell / CELLS_X) | 0;
        const x0 = cx * CELL - BAKE_W / 2, y0 = cy * CELL - BAKE_H / 2;
        const hit = quads.some(q => {
          const xs = [q.corners[0], q.corners[2], q.corners[4], q.corners[6]], ys = [q.corners[1], q.corners[3], q.corners[5], q.corners[7]];
          return Math.max(...xs) > x0 && Math.min(...xs) < x0 + CELL && Math.max(...ys) > y0 && Math.min(...ys) < y0 + CELL;
        });
        if (!hit) continue;
        // CleanUp, then the MainScene layer's two renders.
        target(canvas, 0);
        gl.viewport(0, 0, CELL, CELL);
        gl.disable(gl.BLEND);
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.useProgram(bakeProg);
        gl.uniform1i(gl.getUniformLocation(bakeProg, 'sTexture'), 0);
        if (fullViewport) {
          gl.viewport(-cx * CELL, -cy * CELL, BAKE_W, BAKE_H);
          gl.uniform4f(gl.getUniformLocation(bakeProg, 'xf'), 2 / BAKE_W, 2 / BAKE_H, 0, 0);
        } else {
          gl.uniform4f(gl.getUniformLocation(bakeProg, 'xf'), 2 / CELL, 2 / CELL, -2 * x0 / CELL - 1, -2 * y0 / CELL - 1);
        }
        gl.bindVertexArray(vao);
        gl.activeTexture(gl.TEXTURE0);
        gl.bindSampler(0, sampler);
        gl.enable(gl.BLEND);
        for (const pass of [0, 1]) {
          if (pass === 0) {
            gl.blendFuncSeparate(gl.ONE, gl.ZERO, gl.ZERO, gl.ZERO);
            gl.blendEquationSeparate(gl.FUNC_ADD, gl.FUNC_ADD);
          } else {
            gl.blendFuncSeparate(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE);
            gl.blendEquationSeparate(gl.FUNC_ADD, gl.MAX);
          }
          for (const r of runs) {
            const page = glPages[r.page];
            if (!page) continue;
            gl.bindTexture(gl.TEXTURE_2D, page);
            gl.drawElements(gl.TRIANGLES, r.count * 6, gl.UNSIGNED_INT, r.first * 24);
          }
        }
        gl.bindSampler(0, null);
        gl.disable(gl.BLEND);
        // ConvertTextureToTiledTexture_PC: the cell's state grid rows, then RGB + (state | a3).
        gl.bindTexture(gl.TEXTURE_2D, stateTex);
        if (state) {
          gl.pixelStorei(gl.UNPACK_ROW_LENGTH, BAKE_W);
          gl.pixelStorei(gl.UNPACK_SKIP_PIXELS, cx * CELL);
          gl.pixelStorei(gl.UNPACK_SKIP_ROWS, cy * CELL);
          gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, CELL, CELL, gl.RED_INTEGER, gl.UNSIGNED_BYTE, state);
          gl.pixelStorei(gl.UNPACK_ROW_LENGTH, 0);
          gl.pixelStorei(gl.UNPACK_SKIP_PIXELS, 0);
          gl.pixelStorei(gl.UNPACK_SKIP_ROWS, 0);
        } else {
          gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, CELL, CELL, gl.RED_INTEGER, gl.UNSIGNED_BYTE, new Uint8Array(CELL * CELL));
        }
        const tile = tex2d(gl.RGBA8, CELL, CELL, TILE_LEVELS);
        target(tile, 0);
        gl.viewport(0, 0, CELL, CELL);
        gl.useProgram(convertProg);
        gl.uniform1i(gl.getUniformLocation(convertProg, 'canvas'), 0);
        gl.uniform1i(gl.getUniformLocation(convertProg, 'stateGrid'), 1);
        gl.uniform1uiv(gl.getUniformLocation(convertProg, 'thr'), thr);
        gl.activeTexture(gl.TEXTURE1);
        gl.bindTexture(gl.TEXTURE_2D, stateTex);
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, canvas);
        gl.bindVertexArray(emptyVao);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
        // A cell without a nonzero alpha byte gets no tile.
        gl.bindTexture(gl.TEXTURE_2D, tile);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_BASE_LEVEL, 0);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAX_LEVEL, 0);
        target(flags, 0);
        gl.viewport(0, 0, 64, 64);
        gl.useProgram(reduceProg);
        gl.uniform1i(gl.getUniformLocation(reduceProg, 'tile'), 0);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
        gl.readPixels(0, 0, 64, 64, gl.RGBA, gl.UNSIGNED_BYTE, pixel);
        if (!pixel.some((b, i) => (i & 3) === 0 && b !== 0)) {
          gl.deleteTexture(tile);
          continue;
        }
        // __BuildMipmaps: level l from level l-1 (sampled alone: base = max = l-1).
        gl.useProgram(mipProg);
        gl.uniform1i(gl.getUniformLocation(mipProg, 'src'), 0);
        for (let level = 1; level < TILE_LEVELS; level++) {
          gl.bindTexture(gl.TEXTURE_2D, tile);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_BASE_LEVEL, level - 1);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAX_LEVEL, level - 1);
          target(tile, level);
          gl.viewport(0, 0, CELL >> level, CELL >> level);
          gl.drawArrays(gl.TRIANGLES, 0, 3);
        }
        gl.bindTexture(gl.TEXTURE_2D, tile);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_BASE_LEVEL, 0);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAX_LEVEL, TILE_LEVELS - 1);
        cells[cell] = tile;
        bytes += TILE_BYTES;
      }
    }
  } finally {
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.bindVertexArray(null);
    gl.bindSampler(0, null);
    for (const t of [canvas, stateTex, flags]) gl.deleteTexture(t);
    gl.deleteFramebuffer(fb);
    gl.deleteVertexArray(vao);
    gl.deleteVertexArray(emptyVao);
    gl.deleteBuffer(vbo);
    gl.deleteBuffer(ibo);
    gl.deleteSampler(sampler);
    for (const p of [bakeProg, convertProg, reduceProg, mipProg]) gl.deleteProgram(p);
    renderer.resetState();
  }
  return { tiles, ms: performance.now() - t0, bytes };
}
